import {NumberPlateModel} from "@/model/car-info/number-plate.model";
import {DateTime} from "luxon";

/**
 * サーバーサイドのお車情報を保持するモデルクラスです。
 * クライアントサイドに依存する情報は保持しないでください。
 */
export class CarInfoModel {

    // 最終更新日の日付フォーマット
    private static readonly LAST_MODIFIED_DATETIME = "yyyy-MM-dd HH:mm:ss"

    // お車情報の表示番号
    displayId: string = "";

    // お車情報ID
    id: string = "";

    // ファーストカー区分
    isFirst: boolean = false;

    // メーカー
    maker: string = "";

    // 車種
    type: string = "";

    // 車種の頭文字
    typeInitialLetter: string = "";

    // 年式
    modelYear: string = "";

    // 型式
    model: string = "";

    // フロントタイヤサイズ
    frontTireSize: string = "";

    // リアタイヤサイズ
    reaTireSize: string = "";

    // ボディタイプ
    bodyType: string = "";

    // 前タイヤ リム径
    frontRimDiameter: string = "";

    // 前タイヤ 幅
    frontTireWidth: string = "";

    // 前タイヤ ラジアル
    frontTireRadial: string = "";

    // 前タイヤ 扁平率
    frontTireAspectRatio: string = "";

    // 前タイヤ XL
    frontXL: boolean = false;

    // 前タイヤRFT
    frontRft: boolean = false;

    // 後タイヤ リム径
    reaRimDiameter: string = "";

    // 後タイヤ 幅
    reaTireWidth: string = "";

    // 後タイヤ ラジアル
    reaTireRadial: string = "";

    // 後タイヤ 扁平率
    reaTireAspectRatio: string = "";

    // 後タイヤ XL
    reaXL: boolean = false;

    // 後タイヤ RFT
    reaRft: boolean = false;

    // ナンバープレート
    numberPlate: NumberPlateModel = new NumberPlateModel;

    // 最終更新日
    lastModifiedDatetime: string;

    public copy(obj: CarInfoModel): CarInfoModel {
        if (obj) {
            this.displayId = obj.displayId;
            this.id = obj.id;
            this.isFirst = obj.isFirst;
            this.maker = obj.maker;
            this.type = obj.type;
            this.typeInitialLetter = obj.typeInitialLetter;
            this.modelYear = obj.modelYear || "";
            this.model = obj.model || "";
            this.frontTireSize = obj.frontTireSize;
            this.reaTireSize = obj.reaTireSize;
            this.bodyType = obj.bodyType;
            this.frontRimDiameter = obj.frontRimDiameter || "";
            this.frontTireWidth = obj.frontTireWidth || "";
            this.frontTireAspectRatio = obj.frontTireAspectRatio || "";
            this.frontXL = obj.frontXL || false;
            this.frontRft = obj.frontRft || false;
            this.frontTireRadial = obj.frontTireRadial || "";
            this.reaRimDiameter = obj.reaRimDiameter || "";
            this.reaTireWidth = obj.reaTireWidth || "";
            this.reaTireAspectRatio = obj.reaTireAspectRatio || "";
            this.reaXL = obj.reaXL || false;
            this.reaRft = obj.reaRft || false;
            this.reaTireRadial = obj.reaTireRadial || "";
            this.numberPlate.copy(obj.numberPlate);
            this.lastModifiedDatetime = obj.lastModifiedDatetime;
        }
        return this;
    }

    /** 前タイヤサイズを返却します。 */
    public getFrontTireSize(): string {
        return CarInfoModel.editTireSize(this.frontTireSize, this.frontRimDiameter, this.frontTireWidth, this.frontTireAspectRatio, this.frontRft, this.frontTireRadial);
    }

    /** 後タイヤサイズを返却します。 */
    public getReaTireSize(): string {
        return CarInfoModel.editTireSize(this.reaTireSize, this.reaRimDiameter, this.reaTireWidth, this.reaTireAspectRatio, this.reaRft, this.reaTireRadial);
    }

    /** タイヤサイズを編集します。 */
    public static editTireSize(size, rimDiameter, width, aspectRatio, rft, radial) {
        // タイヤサイズが設定されている場合はそのまま返す。
        if (size) {
            // XLは表示しない
            return size.replace(" XL", "");
        }

        if (!width && !aspectRatio && !rimDiameter) {
            // タイヤサイズ未設定かつ装着タイヤサイズプルダウン(幅・扁平率・リム径)も設定されていない場合
            return "";
        }
        radial = radial || "R";
        if (typeof rft === 'string') {
            rft = rft === "true";
        }
        // タイヤサイズが設定されていないかつ装着タイヤサイズプルダウン(幅・扁平率・リム径)が設定されている場合は各項目を連結編集して返す
        const rftValue = rft ? " RFT" : "";
        return `${width}/${aspectRatio}${radial}${rimDiameter}${rftValue}`;
    }

    /**
     * 最終更新日にUTC時刻をセットします。
     */
    public setLastModifiedDatetimeNow() {
        this.lastModifiedDatetime = DateTime.now().toUTC().toFormat(CarInfoModel.LAST_MODIFIED_DATETIME);
    }

    /**
     * 最終更新日を取得します。
     */
    public getLastModifiedDatetime(): DateTime {
        return DateTime.fromFormat(this.lastModifiedDatetime, CarInfoModel.LAST_MODIFIED_DATETIME);
    }

    /**
     * 前タイヤサイズの値と同じ値を後タイヤに設定します。
     * 前後で異なるサイズのタイヤを利用しているフラグがない場合に使用してください。
     */
    public syncFront() {
        this.reaTireSize = this.frontTireSize;
        this.reaTireWidth = this.frontTireWidth;
        this.reaTireAspectRatio = this.frontTireAspectRatio;
        this.reaRimDiameter = this.frontRimDiameter;
        this.reaXL = this.frontXL;
        this.reaRft = this.frontRft;
    }

    /**
     * 前タイヤの項目をセットします。
     * @param frontTireSizeList 前タイヤのサイズのリスト
     * @param carTypeDetails 車種詳細リスト
     */
    public setFrontTireValue(frontTireSizeList: Array<any>, carTypeDetails: Array<any>) {
        if (this.frontTireSize) {
            // フロントタイヤサイズが設定されている場合は幅・扁平率・リム径・XL・RFTをセットする
            const tireSize = frontTireSizeList.find(data => data.size === this.frontTireSize)
            const carDetail = carTypeDetails.find(data => data.carTypeTireSizeId === tireSize.id)
            this.frontTireWidth = carDetail.tireWidth
            this.frontTireAspectRatio = carDetail.aspectRatio
            this.frontTireRadial = carDetail.radial
            this.frontRimDiameter = carDetail.rimDiameter
            this.frontXL = carDetail.xl
            this.frontRft = carDetail.rft
            this.frontTireSize = ""
            this.frontTireSize = this.getFrontTireSize()
        } else if (this.frontTireWidth && this.frontTireAspectRatio && this.frontRimDiameter) {
            // フロントタイヤサイズが設定されていない、かつ幅・扁平率・リム径が設定されている場合タイヤサイズに確認画面で表示したタイヤサイズと同様の値を設定する。
            this.frontTireRadial = "R"
            this.frontTireSize = CarInfoModel.editTireSize(
              this.frontTireSize,
              this.frontRimDiameter,
              this.frontTireWidth,
              this.frontTireAspectRatio,
              this.frontRft,
              this.frontTireRadial
            )
        }
    }

    /**
     * 後タイヤの項目をセットします。
     * @param reaTireSizeList 後タイヤのサイズのリスト
     * @param carTypeDetails 車種詳細リスト
     */
    public setReaTireValue(reaTireSizeList: Array<any>, carTypeDetails: Array<any>) {
        if (this.reaTireSize) {
            // フロントタイヤサイズが設定されている場合は幅・扁平率・リム径・XL・RFTをセットする
            const tireSize = reaTireSizeList.find(data => data.size === this.reaTireSize)
            const carDetail = carTypeDetails.find(data => data.carTypeTireSizeId === tireSize.id)
            this.reaTireWidth = carDetail.tireWidth
            this.reaTireAspectRatio = carDetail.aspectRatio
            this.reaTireRadial = carDetail.radial
            this.reaRimDiameter = carDetail.rimDiameter
            this.reaXL = carDetail.xl
            this.reaRft = carDetail.rft
            this.reaTireSize = ""
            this.reaTireSize = this.getReaTireSize()
        } else if (this.reaTireWidth && this.reaTireAspectRatio && this.reaRimDiameter) {
            // フロントタイヤサイズが設定されていない、かつ幅・扁平率・リム径が設定されている場合タイヤサイズに確認画面で表示したタイヤサイズと同様の値を設定する。
            this.reaTireRadial = "R"
            this.reaTireSize = CarInfoModel.editTireSize(
              this.reaTireSize,
              this.reaRimDiameter,
              this.reaTireWidth,
              this.reaTireAspectRatio,
              this.reaRft,
              this.reaTireRadial
            )
        }
    }
}